import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { useSeedMatchScore } from '../../api/useMatchEvents';
import { buildSeedDto, byHalfHasScore, num } from '../../utils/seedScore';

const digits = (v: string) => v.replace(/[^0-9]/g, '');

/** Compact single-digit-ish numeric input shared across the control. */
function Num({
  label,
  value,
  onChange,
  ariaLabel,
  width = 44,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  width?: number;
}) {
  return (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(digits(e.target.value))}
      inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': ariaLabel }}
      sx={{
        width,
        '& .MuiInputBase-input': { py: 0.35, px: 0.5, fontSize: 12, textAlign: 'center' },
        '& .MuiInputLabel-root': { fontSize: 10.5 },
        '& .MuiInputLabel-shrink': { fontSize: 11 },
      }}
    />
  );
}

const dash = (
  <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>
    –
  </Typography>
);

/**
 * Operator "set score" control for establishing (or correcting) the backend
 * `MatchState` baseline when the LSports feed cold-started mid-match and no
 * authoritative score exists yet (the `Incidents` feed has no replay/backfill on
 * connect, so a pre-connect goal is never re-sent). It POSTs to
 * `POST /match-events/matches/:fixtureId/seed-score`; the engine interprets the
 * seed and re-publishes the authoritative `MatchState` over the `match_state` WS
 * event — the FE renders that backend state, NEVER the typed values directly.
 *
 * Two entry modes:
 * - **Global**: the MATCH TOTAL. LSports scores per period, so a global seed
 *   can't know the current period's split — the engine leaves an anchor that
 *   absorbs the first post-seed reading (a goal in that window is folded into the
 *   total, never counted, never fired). Kept only for the rare case of joining in
 *   the 2nd half without knowing the half-time split.
 * - **By half** (`Por tiempo`, the DEFAULT): the exact 1H/2H breakdown. The
 *   engine seeds each period bucket directly — no anchor, so every later goal
 *   counts AND fires. The global total is derived from the halves.
 *
 * By-half is the default because a global seed taken mid-match SYSTEMATICALLY
 * absorbs the very next goal (verified live on fixtures 19463946 + 19377335:
 * seeded 0-0 in the 1st half, the goal was folded into the seeded total and never
 * fired). For a 1st-half match filling by-half is trivial — the current score in
 * 1H, 2H left 0.
 *
 * `mode: 'seed'` (no baseline yet) opens the inputs by default. `mode: 'override'`
 * (a baseline exists — the feed can mis-state a score) starts collapsed behind a
 * "Correct score" button. Corners are optional; scores are required. Failures
 * surface through the global mutation-error toast (the mutation carries a
 * `mutationKey`) — never a silent failure.
 */
export function SeedScoreControl({
  fixtureId,
  homeTeam,
  awayTeam,
  mode,
  showCorners = false,
}: {
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
  mode: 'seed' | 'override';
  /**
   * Show the corner inputs. Off for a goals-only match (no `*_corners` sniper
   * market) — then the seed is score-only and the corner sound is muted upstream.
   */
  showCorners?: boolean;
}) {
  const seed = useSeedMatchScore();
  const [open, setOpen] = useState(mode === 'seed');
  // Default to by-half: a global seed mid-match absorbs the next goal (see the
  // component doc). The operator can still flip to global for a 2nd-half join
  // without the half-time split.
  const [byHalf, setByHalf] = useState(true);

  // Global (total) fields.
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const [homeCorners, setHomeCorners] = useState('');
  const [awayCorners, setAwayCorners] = useState('');

  // Per-half fields — blank, treated as 0 by `buildSeedDto`. Kept blank (not '0')
  // so an accidental "Set" the instant "Por tiempo" is flipped can't clobber a
  // live baseline with 0–0: `canSubmit` requires at least one SCORE field typed
  // (`byHalfHasScore` — a corner alone won't do it), and an explicit 0–0 seed
  // still works by typing a `0`.
  const [h1, setH1] = useState('');
  const [a1, setA1] = useState('');
  const [h2, setH2] = useState('');
  const [a2, setA2] = useState('');
  const [ch1, setCh1] = useState('');
  const [ca1, setCa1] = useState('');
  const [ch2, setCh2] = useState('');
  const [ca2, setCa2] = useState('');

  const canSubmit = seed.isLoading
    ? false
    : byHalf
      ? byHalfHasScore({ h1, a1, h2, a2 })
      : home !== '' && away !== '';

  const reset = () => {
    setHome('');
    setAway('');
    setHomeCorners('');
    setAwayCorners('');
    setH1('');
    setA1('');
    setH2('');
    setA2('');
    setCh1('');
    setCa1('');
    setCh2('');
    setCa2('');
    setByHalf(true); // back to the default, not global
  };

  const submit = () => {
    if (!canSubmit) return;
    const dto = buildSeedDto({
      byHalf,
      home,
      away,
      homeCorners,
      awayCorners,
      h1,
      a1,
      h2,
      a2,
      ch1,
      ca1,
      ch2,
      ca2,
    });
    seed.mutate(
      { fixtureId, dto },
      {
        onSuccess: () => {
          reset();
          // A corrected baseline collapses back; a fresh seed leaves the inputs
          // open until the backend state arrives and swaps this out for the board.
          if (mode === 'override') setOpen(false);
        },
      },
    );
  };

  if (!open) {
    return (
      <Button
        size="small"
        variant="text"
        startIcon={<SportsScoreIcon fontSize="small" />}
        onClick={() => setOpen(true)}
      >
        Correct score
      </Button>
    );
  }

  // Only a real derived total once a score is typed — an empty by-half control
  // must NOT show "0–0", which reads as a fabricated scoreline (and tripped the
  // hub's no-fabricated-0–0 guard).
  const hasHalfScore = byHalfHasScore({ h1, a1, h2, a2 });

  // The two per-half groups, described declaratively so the score + corner rows
  // (and their aria-labels) stay in lockstep and are rendered once.
  const halves = [
    { key: '1H', h: h1, setH: setH1, a: a1, setA: setA1, ch: ch1, setCh: setCh1, ca: ca1, setCa: setCa1 },
    { key: '2H', h: h2, setH: setH2, a: a2, setA: setA2, ch: ch2, setCh: setCh2, ca: ca2, setCa: setCa2 },
  ];

  /** A corner input pair, only when `showCorners`. */
  const cornerPair = (
    prefix: string,
    ch: string,
    setCh: (v: string) => void,
    ca: string,
    setCa: (v: string) => void,
  ) =>
    showCorners ? (
      <Stack direction="row" spacing={0.25} alignItems="center">
        <Typography component="span" sx={{ fontSize: 12, opacity: 0.7, mr: 0.25 }} title="corners">
          ⚑
        </Typography>
        <Num
          label="H"
          value={ch}
          onChange={setCh}
          ariaLabel={prefix ? `${prefix} home corners` : 'home corners'}
        />
        {dash}
        <Num
          label="A"
          value={ca}
          onChange={setCa}
          ariaLabel={prefix ? `${prefix} away corners` : 'away corners'}
        />
      </Stack>
    ) : null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1,
        py: 0.75,
        bgcolor: 'action.hover',
      }}
    >
      {/* Header: title · derived total · Por tiempo toggle */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary' }}>
          SET SCORE
        </Typography>
        {byHalf &&
          (hasHalfScore ? (
            <Chip
              size="small"
              label={`${num(h1) + num(h2)}–${num(a1) + num(a2)}`}
              sx={{ height: 20, fontWeight: 700, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              carga 1H / 2H
            </Typography>
          ))}
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: 11 } }}
          control={
            <Switch
              size="small"
              checked={byHalf}
              onChange={(e) => setByHalf(e.target.checked)}
              inputProps={{ 'aria-label': 'seed by half' }}
            />
          }
          label="Por tiempo"
        />
      </Stack>

      {/* Inputs + actions on one aligned row */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {byHalf ? (
          halves.map((g) => (
            <Stack key={g.key} direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 18 }}>
                {g.key}
              </Typography>
              <Num label="H" value={g.h} onChange={g.setH} ariaLabel={`${g.key} home score`} />
              {dash}
              <Num label="A" value={g.a} onChange={g.setA} ariaLabel={`${g.key} away score`} />
              {cornerPair(g.key, g.ch, g.setCh, g.ca, g.setCa)}
            </Stack>
          ))
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Num label={homeTeam || 'H'} value={home} onChange={setHome} ariaLabel="home score" width={56} />
            {dash}
            <Num label={awayTeam || 'A'} value={away} onChange={setAway} ariaLabel="away score" width={56} />
            {cornerPair('', homeCorners, setHomeCorners, awayCorners, setAwayCorners)}
          </Stack>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button size="small" variant="contained" onClick={submit} disabled={!canSubmit} sx={{ minWidth: 0, px: 1.5 }}>
          {seed.isLoading ? '…' : 'Set'}
        </Button>
        {mode === 'override' && (
          <Button
            size="small"
            onClick={() => {
              reset();
              setOpen(false);
            }}
            disabled={seed.isLoading}
            sx={{ minWidth: 0, px: 1 }}
          >
            Cancel
          </Button>
        )}
      </Stack>
    </Box>
  );
}
